import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Theme Management
  useEffect(() => {
    const savedTheme = localStorage.getItem('msb-theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    } else {
      // Auto-detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(prefersDark);
    }
  }, []);

  useEffect(() => {
    // Apply theme to document
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('msb-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // API Helper Functions
  const apiRequest = async (endpoint, options = {}) => {
    const token = localStorage.getItem('msb-token');

    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    // Für FormData darf der 'Content-Type' Header nicht manuell gesetzt werden
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Spezielle Behandlung für 401-Fehler (Token abgelaufen)
        if (response.status === 401) {
          console.warn('Token abgelaufen, leite zur Neuanmeldung weiter...');
          // Token aus localStorage entfernen
          localStorage.removeItem('msb-token');
          localStorage.removeItem('msb-user');
          localStorage.removeItem('msb-session-id');
          // Benutzer zur Neuanmeldung zwingen
          setUser(null);
          setSessionId(null);
          toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
          return;
        }

        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      // Behandelt leere Antworten
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  };

  // Check for existing session
  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem('msb-token');
      const savedUser = localStorage.getItem('msb-user');
      const savedSessionId = localStorage.getItem('msb-session-id');

      if (token && savedUser && savedSessionId) {
        try {
          const response = await apiRequest('/auth/validate');
          if (response.success) {
            setUser(response.user);
            setSessionId(savedSessionId);
          } else {
            // Session invalid, clear storage
            localStorage.removeItem('msb-token');
            localStorage.removeItem('msb-user');
            localStorage.removeItem('msb-session-id');
          }
        } catch (error) {
          console.error('Session validation failed:', error);
          // Clear invalid session
          localStorage.removeItem('msb-token');
          localStorage.removeItem('msb-user');
          localStorage.removeItem('msb-session-id');
        }
      }
      setIsLoading(false);
    };

    validateSession();
  }, []);

  const login = async (username, password) => {
    setIsLoading(true);

    try {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });

      if (response.success) {
        const { user: userData, session } = response;

        // Store session data
        localStorage.setItem('msb-token', session.token);
        localStorage.setItem('msb-user', JSON.stringify(userData));
        localStorage.setItem('msb-session-id', session.sessionId);

        setUser(userData);
        setSessionId(session.sessionId);

        toast.success(`Willkommen zurück, ${userData.firstName || userData.username}!`);
        return { success: true };
      }
      toast.error('Anmeldung fehlgeschlagen');
      return { success: false, error: response.message };
    } catch (error) {
      toast.error(error.message || 'Anmeldung fehlgeschlagen');
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear local storage regardless of API response
      setUser(null);
      setSessionId(null);
      localStorage.removeItem('msb-token');
      localStorage.removeItem('msb-user');
      localStorage.removeItem('msb-session-id');
      toast.success('Erfolgreich abgemeldet');
    }
  };

  const register = async (userData) => {
    setIsLoading(true);

    try {
      const response = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      if (response.success) {
        const { user: newUser, session } = response;

        // Auto-login after registration
        localStorage.setItem('msb-token', session.token);
        localStorage.setItem('msb-user', JSON.stringify(newUser));
        localStorage.setItem('msb-session-id', session.sessionId);

        setUser(newUser);
        setSessionId(session.sessionId);

        toast.success(
          `Benutzer ${newUser.firstName || newUser.username} erfolgreich erstellt und angemeldet`,
        );
        return { success: true, user: newUser };
      }
      toast.error('Registrierung fehlgeschlagen');
      return { success: false, error: response.message };
    } catch (error) {
      toast.error(error.message || 'Registrierung fehlgeschlagen');
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await apiRequest('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (response.success) {
        toast.success('Passwort erfolgreich geändert');
        // Force re-login after password change
        await logout();
        return { success: true };
      }
      toast.error('Passwort-Änderung fehlgeschlagen');
      return { success: false, error: response.message };
    } catch (error) {
      toast.error(error.message || 'Passwort-Änderung fehlgeschlagen');
      return { success: false, error: error.message };
    }
  };

  const syncRadManUsers = async (radmanUsers) => {
    try {
      const response = await apiRequest('/auth/sync-radman', {
        method: 'POST',
        body: JSON.stringify({ radmanUsers }),
      });

      if (response.success) {
        toast.success(
          `RadMan Synchronisation abgeschlossen: ${response.syncedUsers.length} Benutzer verarbeitet`,
        );
        return { success: true, syncedUsers: response.syncedUsers };
      }
      toast.error('RadMan Synchronisation fehlgeschlagen');
      return { success: false, error: response.message };
    } catch (error) {
      toast.error(error.message || 'RadMan Synchronisation fehlgeschlagen');
      return { success: false, error: error.message };
    }
  };

  const getUserStats = async (days = 30) => {
    try {
      const response = await apiRequest(`/auth/stats?days=${days}`);
      return response.stats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return null;
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiRequest('/auth/users');
      if (response.success) {
        setUsers(response.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    }
  };

  const createUser = async (userData) => {
    try {
      const response = await apiRequest('/auth/users', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      if (response.success) {
        toast.success('Benutzer erfolgreich erstellt');
        await loadUsers(); // Reload users list
        return { success: true, user: response.user };
      }
      toast.error(response.message || 'Fehler beim Erstellen des Benutzers');
      return { success: false, error: response.message };
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Fehler beim Erstellen des Benutzers');
      return { success: false, error: error.message };
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      const response = await apiRequest(`/auth/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      if (response.success) {
        toast.success('Benutzer erfolgreich aktualisiert');
        await loadUsers(); // Reload users list
        return { success: true, user: response.user };
      }
      toast.error(response.message || 'Fehler beim Aktualisieren des Benutzers');
      return { success: false, error: response.message };
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Fehler beim Aktualisieren des Benutzers');
      return { success: false, error: error.message };
    }
  };

  const deleteUser = async (userId, options = {}) => {
    if (userId === user?.id) {
      toast.error('Sie können sich nicht selbst löschen');
      return { success: false, error: 'Cannot delete own account' };
    }

    try {
      const response = await apiRequest(`/auth/users/${userId}`, {
        method: 'DELETE',
        body: JSON.stringify({
          deleteDocuments: options.deleteDocuments || false,
          anonymizeDocuments: options.anonymizeDocuments || false,
        }),
      });

      if (response.success) {
        const details = response.details || {};
        let message = 'Benutzer erfolgreich gelöscht';

        if (details.analyticsDeleted) {
          message += ' (Analytics-Daten gelöscht)';
        }

        if (details.documentsHandled === 'deleted') {
          message += ' (Dokumente gelöscht)';
        } else if (details.documentsHandled === 'anonymized') {
          message += ' (Dokumente anonymisiert)';
        }

        toast.success(message);
        await loadUsers(); // Reload users list
        return { success: true, details };
      }
      toast.error(response.message || 'Fehler beim Löschen des Benutzers');
      return { success: false, error: response.message };
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Fehler beim Löschen des Benutzers');
      return { success: false, error: error.message };
    }
  };

  const toggleUserStatus = async (userId) => {
    if (userId === user?.id) {
      toast.error('Sie können Ihren eigenen Status nicht ändern');
      return { success: false, error: 'Cannot toggle own status' };
    }

    try {
      const response = await apiRequest(`/auth/users/${userId}/toggle`, {
        method: 'POST',
      });

      if (response.success) {
        toast.success('Benutzerstatus erfolgreich geändert');
        await loadUsers(); // Reload users list
        return { success: true, user: response.user };
      }
      toast.error(response.message || 'Fehler beim Ändern des Benutzerstatus');
      return { success: false, error: response.message };
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Fehler beim Ändern des Benutzerstatus');
      return { success: false, error: error.message };
    }
  };

  const changeUserPassword = async (userId, newPassword) => {
    try {
      const response = await apiRequest(`/auth/users/${userId}/password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword }),
      });

      if (response.success) {
        toast.success('Passwort erfolgreich geändert');
        return { success: true };
      }
      toast.error(response.message || 'Fehler beim Ändern des Passworts');
      return { success: false, error: response.message };
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Fehler beim Ändern des Passworts');
      return { success: false, error: error.message };
    }
  };

  const updateCompany = async (companyId, updates) => {
    try {
      const response = await apiRequest(`/companies/${companyId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      if (response.success) {
        return { success: true, company: response.company };
      }
      toast.error(response.message || 'Fehler beim Aktualisieren der Firma');
      return { success: false, error: response.message };
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Fehler beim Aktualisieren der Firma');
      return { success: false, error: error.message };
    }
  };

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const value = {
    user,
    users,
    isLoading,
    isDarkMode,
    sessionId,
    login,
    logout,
    register,
    changePassword,
    changeUserPassword,
    syncRadManUsers,
    getUserStats,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    updateCompany,
    toggleTheme,
    apiRequest, // Export API helper for other components
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
